import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPinOff } from "lucide-react";

// Záložka „Místo“ ve schvalování docházky: mapa se stavbou (okruh), bodem
// příchodu a odchodu. Pod mapou dvě tlačítka — vybraný bod se zvýrazní,
// mapa se na něj posune a pod ní je jeho čas a vzdálenost od stavby.
// Používá už uložené souřadnice, nic nového se nesbírá.
const pin = (letter, bg, active) => L.divIcon({
  className: "",
  html: `<div style="width:26px;height:26px;border-radius:50%;background:${bg};color:#fff;display:grid;place-items:center;font:800 12px Inter,sans-serif;border:2.5px solid #fff;box-shadow:0 0 0 ${active ? 4 : 1}px ${active ? bg + "55" : "rgba(0,0,0,.3)"};opacity:${active ? 1 : .55}">${letter}</div>`,
  iconSize: [26, 26], iconAnchor: [13, 13],
});
const siteIcon = (letter = "S") => L.divIcon({
  className: "",
  html: `<div style="width:22px;height:22px;border-radius:6px;background:#17171a;color:#fff;display:grid;place-items:center;font:800 11px Inter,sans-serif;border:2px solid #fff">${letter}</div>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

const fmtDist = (m) => (m == null ? null : m >= 1000 ? `${(m / 1000).toLocaleString("cs-CZ", { maximumFractionDigits: 1 })} km` : `${Math.round(m)} m`);

export function PresenceMap({ sites = [], radius = 300, points = [], testid = "presence" }) {
  const withGps = points.filter((p) => p.lat != null && p.lng != null);
  const [sel, setSel] = useState(withGps[0]?.key || points[0]?.key);
  const ref = useRef(null);
  const mapRef = useRef(null);
  const selPoint = points.find((p) => p.key === sel) || points[0];
  const placedSites = sites.filter((s) => s.lat != null && s.lng != null);
  const hasMap = withGps.length > 0 || placedSites.length > 0;

  useEffect(() => {
    if (!hasMap || !ref.current) return;
    if (!mapRef.current) {
      mapRef.current = L.map(ref.current, { zoomControl: false, attributionControl: false });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(mapRef.current);
    }
    const map = mapRef.current;
    (map._pl || []).forEach((l) => map.removeLayer(l));
    map._pl = [];
    placedSites.forEach((s, i) => {
      map._pl.push(L.circle([s.lat, s.lng], { radius, color: "#e0703f", weight: 1.5, dashArray: "5 4", fillColor: "#e0703f", fillOpacity: 0.08 }).addTo(map));
      map._pl.push(L.marker([s.lat, s.lng], { icon: siteIcon(placedSites.length > 1 ? String(i + 1) : "S") }).addTo(map));
    });
    withGps.forEach((p) => map._pl.push(L.marker([p.lat, p.lng], { icon: pin(p.letter, p.color, p.key === sel), zIndexOffset: p.key === sel ? 1000 : 0 }).addTo(map)));
    const all = [...placedSites, ...withGps].map((x) => [x.lat, x.lng]);
    const focus = withGps.find((p) => p.key === sel);
    if (all.length > 1) map.fitBounds(all, { padding: [36, 36], maxZoom: 17 });
    else map.setView(all[0], 16);
    if (focus && all.length > 1) map.panTo([focus.lat, focus.lng], { animate: !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches });
    setTimeout(() => map.invalidateSize(), 120);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMap, sel, JSON.stringify(points), JSON.stringify(sites), radius]);

  useEffect(() => () => { mapRef.current?.remove(); mapRef.current = null; }, []);

  return (
    <div className="space-y-2.5" data-testid={testid}>
      {hasMap ? (
        <div ref={ref} className="h-[220px] md:h-[280px] w-full rounded-[16px] overflow-hidden border border-border isolate" data-testid={`${testid}-map`} />
      ) : (
        <div className="h-[140px] rounded-[16px] border border-dashed border-border grid place-items-center text-center text-sm text-muted-foreground px-4" data-testid={`${testid}-nomap`}>
          <span className="flex flex-col items-center gap-1.5"><MapPinOff size={20} />Poloha nebyla zaznamenána (bez GPS nebo stavba nemá adresu na mapě).</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        {points.map((p) => {
          const on = p.key === sel;
          return (
            <button key={p.key} type="button" onClick={() => setSel(p.key)} data-testid={`${testid}-btn-${p.key}`}
              className={`h-12 min-w-0 rounded-[14px] bg-card flex items-center justify-center gap-2 px-2 font-bold text-[14px] tabular-nums transition-colors ${on ? "border-2 border-foreground" : "border border-border hover:bg-secondary"}`}>
              <span className="w-5 h-5 rounded-full grid place-items-center text-white text-[10px] font-extrabold shrink-0" style={{ background: p.color }}>{p.letter}</span>
              <span className="truncate">{p.label} {p.time}</span>
              {p.out && <span className="text-amber-600 dark:text-amber-400 shrink-0">⚠</span>}
            </button>
          );
        })}
      </div>

      {selPoint && (
        <div className="rounded-[14px] border border-border bg-card p-3.5 space-y-1.5 text-[14px]" data-testid={`${testid}-info`}>
          <div className="flex justify-between gap-3"><span className="text-muted-foreground">Čas</span><b className="tabular-nums text-right">{selPoint.time || "—"}{selPoint.note ? ` · ${selPoint.note}` : ""}</b></div>
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">Od stavby</span>
            {selPoint.lat == null ? <b className="text-muted-foreground">bez GPS</b>
              : selPoint.distance_m == null ? <b className="text-muted-foreground">stavba nemá polohu</b>
              : <b className={`tabular-nums text-right ${selPoint.out ? "text-amber-600 dark:text-amber-400" : "text-success"}`} data-testid={`${testid}-dist`}>
                  {selPoint.siteName ? `${selPoint.siteName} · ` : ""}{fmtDist(selPoint.distance_m)} {selPoint.out ? "· mimo okruh ⚠" : "✓"}
                </b>}
          </div>
          <div className="flex justify-between gap-3"><span className="text-muted-foreground">Okruh stavby</span><b className="tabular-nums">{fmtDist(radius)}</b></div>
        </div>
      )}
    </div>
  );
}

// Vzdálenost dvou bodů v metrech (pro denní výkaz — body se počítají na klientu).
export function haversineM(a, b) {
  const R = 6371000, rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat), dLng = rad(b.lng - a.lng);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}
